import { CheckOutlined, NumbersOutlined } from "@mui/icons-material";
import { Box, Button, Grid2, Stack } from "@mui/material";
import { useCustomMutation, useNotification } from "@refinedev/core";
import { useState } from "react";
import { EditView } from "../../components/EditView";
import { useStoreForm } from "../../components/ports/form/usePortForm";
import { PortInfoCard, PortStatusCard } from "../../components/ports/info-card";

const EditControls = ({
  isEditing,
  onEdit,
  onCancel,
  onSave,
}: {
  isEditing: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
}) =>
  isEditing ? (
    <Stack direction="row" justifyContent="space-between">
      <Button
        variant="outlined"
        color="error"
        startIcon={<NumbersOutlined />}
        onClick={onCancel}
      >
        Cancel
      </Button>
      <Button variant="outlined" startIcon={<CheckOutlined />} onClick={onSave}>
        Save
      </Button>
    </Stack>
  ) : (
    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
      <Button
        variant="outlined"
        startIcon={<NumbersOutlined />}
        onClick={onEdit}
      >
        {onEdit.name === "setIsEditingStatus" ? "Change status" : "Edit"}
      </Button>
    </Box>
  );

export const SectionBox = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      outline: "1px solid",
      outlineColor: "divider",
      borderRadius: 1,
      p: 2,
    }}
  >
    {children}
  </Box>
);

export const EditPort = () => {
  const form = useStoreForm();

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isEditingStatus, setIsEditingStatus] = useState<boolean>(false);

  const port = form.refineCore.query?.data?.data;

  const { mutate } = useCustomMutation();

  const { open } = useNotification();

  const handleSave = (values: any) => {
    mutate(
      {
        url: `${import.meta.env.VITE_API_URL}/api/odata/AdminPorts/${
          port?.id
        }/UpdatePortDetails`,
        method: "post",
        values: {
          Code: values.code,
          Name: values.name,
          CountryCode: values.countryCode,
        },
      },
      {
        onSuccess: () => {
          open?.({
            type: "success",
            message: "Port updated",
            description: "The port details were updated successfully.",
          });
          form.refineCore.query?.refetch();
          setIsEditing(false);
        },
        onError: async (error) => {
          let message = "An unexpected error occurred.";

          try {
            const errorBody = await error.json();
            message = errorBody.error.message;
          } catch (e) {
            message = error?.message || message;
          }

          open?.({
            type: "error",
            message: "Failed to update port",
            description: message,
          });
        },
      }
    );
  };

  const updateStatus = (values: any) => {
    mutate(
      {
        url: `${import.meta.env.VITE_API_URL}/api/odata/AdminPorts/${
          port?.id
        }/UpdateDeleteState`,
        method: "post",
        values: {
          IsDeleted: values.isDeleted,
        },
      },
      {
        onSuccess: () => {
          form.refineCore.query?.refetch();
          setIsEditingStatus(false);
          open?.({
            type: "success",
            message: "Status updated",
            description: "The status was updated",
          });
        },
      }
    );
  };

  return (
    <EditView name={port?.name}>
      <Grid2
        size={{ xs: 12, lg: 8 }}
        spacing={2}
        direction={"column"}
        container
      >
        <Box>
          <SectionBox>
            <PortInfoCard form={form} isEditing={isEditing} port={port} />
          </SectionBox>
        </Box>
        <EditControls
          isEditing={isEditing}
          onEdit={() => setIsEditing(true)}
          onCancel={() => setIsEditing(false)}
          onSave={form.handleSubmit(handleSave)}
        />
      </Grid2>
      <Grid2
        size={{ xs: 12, lg: 4 }}
        spacing={2}
        direction={"column"}
        container
      >
        <SectionBox>
          <PortStatusCard form={form} isEditing={isEditingStatus} port={port} />
        </SectionBox>
        <EditControls
          isEditing={isEditingStatus}
          onEdit={() => setIsEditingStatus(true)}
          onCancel={() => setIsEditingStatus(false)}
          onSave={form.handleSubmit(updateStatus)}
        />
      </Grid2>
    </EditView>
  );
};
